// Project type definition
export interface Project {
  id: string;
  title: string;
  description: string;
  kpi: string[];  // Array of KPI metrics
  featuredKpi: string; // Most impactful KPI for marketing
  stack: string[];
  link: string;
  image: string;  // URL to project screenshot
  year?: number;
  story?: string;
  challenges?: string[];
  solutions?: string[];
  results?: string[];
  vertical: string; // Industry vertical
}

// Project data
export const projectData: Project[] = [
  {
    "id": "startbahn",
    "title": "Startbahn",
    "description": "Fractional NFT ownership platform achieving 15 % gas savings and immutable Startrail provenance on Ethereum.",
    "kpi": [
      "15% Gas Reduction",
      "US$ 13.2M Total Funding",
      "4 Major NFT Campaigns",
      "0 Security Incidents"
    ],
    "featuredKpi": "US$ 13.2M Total Funding",
    "vertical": "Art Market & Blockchain",
    "stack": [
      "Solidity",
      "Ethereum",
      "Web3.js",
      "NestJS",
      "Node.js",
      "IPFS",
      "Startrail API",
      "React",
      "Next.js",
      "Google Cloud KMS"
    ],
    "link": "https://startbahn.io",
    "image": "/projectScreens/startbahn.png",
    "year": 2022,
    "story": "From Nov 2021–Oct 2022, I architected and deployed Startbahn's multi-access smart-contract suite atop Startrail, enabling galleries, artists and institutions to fractionally own and trade NFTs with on-chain provenance checks. The platform powered high-profile campaigns—ATAMI Art Grant scavenger hunts, Toyota Automobile Museum QR-to-NFT hunts, and WEC Fuji Metaverse fan rewards—while ensuring seamless IPFS metadata integration and audit-ready compliance.",
    "challenges": [
      "Designing a tiered, multi-access smart-contract architecture for secure fractional governance",
      "Minimizing Ethereum gas costs under enterprise workloads without compromising security",
      "Seamlessly integrating on-chain provenance with off-chain IPFS metadata and third-party APIs"
    ],
    "solutions": [
      "Architected specialized Solidity contracts with gas-efficient execution paths and role-based access controls",
      "Implemented end-to-end automated testing, formal security audits and continuous compliance checks",
      "Built a flexible fractional-ownership engine with secure transfer, rebalance and on-chain provenance verification via Startrail API"
    ],
    "results": [
      "15% reduction in average gas consumption through optimized contract patterns",
      "Secured US$ 13.2 million across Series A & B funding rounds to scale R&D and platform enhancements",
      "Powered 4 major NFT engagement campaigns (ATAMI Art Grant, Toyota Automobile Museum, WEC Fuji Metaverse, Immortal Words)",
      "Zero security incidents in over 18 months of production on Ethereum mainnet"
    ]
  },
  {
    "id": "chainapi",
    "title": "Chain API",
    "description": "No-code Airnode oracle builder slashing setup from 2 h to 10 min for 250+ providers.",
    "kpi": [
      "Setup Time: 2 h → < 10 min",
      "250+ API Providers",
      "722 k USDC Grant"
    ],
    "featuredKpi": "Setup Time: 2 h → < 10 min",
    "vertical": "Web3 Infrastructure",
    "stack": [
      "Elixir",
      "Phoenix",
      "Next.js",
      "TypeScript",
      "PostgreSQL",
      "Amazon Web Services",
      "Google Cloud Platform",
      "Terraform",
      "GraphQL",
      "API3 Airnode"
    ],
    "link": "https://chainapi.com",
    "image": "/projectScreens/chainapi.png",
    "year": 2021,
    "story": "As Senior Software Engineer, I led end-to-end architecture of the Elixir/Phoenix backend and Next.js dashboard, authoring a Terraform-style scaffold generator that transforms user inputs into Airnode config files—cutting oracle setup from ~2 hours to under 10 minutes. I also built real-time health-metrics reporting via WebSockets + PostgreSQL and implemented GitHub Actions → AWS blue-green deployments, reducing release downtime to under 30 seconds.",
    "challenges": [
      "Automating a traditionally manual, 2-hour Airnode deployment workflow into an intuitive no-code experience",
      "Ensuring compatibility across diverse legacy APIs and multiple EVM networks",
      "Maintaining high availability and observability for decentralized oracle services"
    ],
    "solutions": [
      "Engineered Elixir/Phoenix microservices to orchestrate on-demand Airnode provisioning with auto-generated Terraform JSON configs",
      "Developed a React/Next.js one-click wizard for seamless API-to-oracle binding and multi-cloud (AWS/GCP) deployment",
      "Integrated WebSockets + PostgreSQL for real-time node health and latency dashboards, backed by CI/CD blue-green pipelines"
    ],
    "results": [
      "Reduced average setup time by 92% (from ~2 h to < 10 min)",
      "Onboarded 250+ API providers within six months of launch",
      "Awarded 722 k USDC via API3 DAO Proposal #77 for developer tooling impact",
      "Enabled zero-gas-fee oracle maintenance, cutting OPEX by up to 80%",
      "Scaled to 10 000+ oracle calls per minute under load tests"
    ]
  },{
    "id": "appwork",
    "title": "AppWork",
    "description": "Cloud-native maintenance SaaS cutting API latency 25 % and backing a US$ 13 M Series A.",
    "kpi": [
      "API Latency –25%",
      "Test Coverage 62→88%",
      "CI Build Time –35%",
      "MTTR 45 m→10 m",
      "US$ 13 M Series A",
      "G2 Rank #26"
    ],
    "featuredKpi": "US$ 13 M Series A",
    "vertical": "Property Management",
    "stack": [
      "Elixir",
      "Phoenix",
      "React",
      "React Native",
      "PostgreSQL",
      "Oban",
      "Sentry",
      "GitHub Actions",
      "AWS",
      "ClickUp"
    ],
    "link": "https://appwork.com",
    "image": "/projectScreens/appwork.png",
    "year": 2021,
    "story": "From 2021–2024, as Lead Software Engineer, I transformed AppWork into a cloud-native, mobile-first maintenance operations hub for multifamily property managers. I refactored legacy Elixir/Phoenix services, integrated a React Native mobile app, and orchestrated end-to-end observability with Sentry and Oban—unlocking seamless workflows across RealPage, Yardi and MRI integrations to streamline operations at scale.",
    "challenges": [
      "Refactoring monolithic Phoenix services and upgrading 40+ dependencies to support enterprise-scale workloads",
      "Achieving sub-second API latency under high concurrency without inflating infrastructure costs",
      "Ensuring robust reliability and comprehensive test coverage across a broad feature set, from work-order tracking to compliance reporting"
    ],
    "solutions": [
      "Upgraded core Elixir/Phoenix architecture and introduced property-based and end-to-end tests, boosting coverage from 62% to 88%",
      "Implemented Sentry-based observability dashboards and optimized CI/CD via GitHub Actions, cutting build times by 35% and MTTR from 45 m to 10 m",
      "Conducted bi-weekly performance reviews and query optimizations to reduce median API latency by 25%"
    ],
    "results": [
      "Reduced median API response times by 25% for high-volume workflows",
      "Secured US$ 13 M Series A funding led by Resolve Growth Partners in April 2025",
      "Ranked 26th on G2's 2025 Best Real Estate Products list, validating market fit and user satisfaction",
      "Recognized across four seasonal Top Maintenance Rating Badge programs, underscoring industry leadership"
    ]
  },{
    "id": "open-letter-connect",
    "title": "Open Letter Connect",
    "description": "Serverless direct-mail engine enabling unlimited USPS-verified campaigns with 99.8 % delivery success.",
    "kpi": [
      "Unlimited Campaigns",
      "99.8% Delivery Rate",
      "2× Faster Campaign Setup",
      "HubSpot & Salesforce Sync"
    ],
    "featuredKpi": "99.8% Delivery Rate",
    "vertical": "Direct Mail Marketing",
    "stack": [
      "Node.js",
      "Express",
      "React",
      "AWS Lambda",
      "AWS SQS",
      "Serverless Framework",
      "AWS API Gateway",
      "WebSockets",
      "USPS Address Validation API",
      "HubSpot API",
      "Salesforce API"
    ],
    "link": "https://openletterconnect.com",
    "image": "/projectScreens/open-letter-connect.png",
    "year": 2024,
    "story": "As Technical Lead, I architected and implemented a horizontally auto-scaling mail-processing backend on AWS Lambda, enabling unlimited direct-mail campaigns. I designed a dedicated microservice for USPS-verified address validation to boost deliverability, built a personalization engine for dynamic variable-data injection, and created a pluggable CRM connector framework for HubSpot and Salesforce. On the front end, I led development of a React-based drag-and-drop template builder and integrated a real-time analytics dashboard via WebSockets to provide sub-second campaign metrics and insights. :contentReference[oaicite:0]{index=0}",
    "challenges": [
      "Designing a fully autoscaling, multi-tenant mail backend to support unlimited concurrent campaigns",
      "Achieving 99.8% delivery through real-time USPS address validation and retry logic",
      "Integrating dynamic personalization and bi-directional CRM sync without adding friction to the user workflow",
      "Delivering real-time analytics for campaign performance with sub-second metrics under high load"
    ],
    "solutions": [
      "Engineered an AWS Lambda + SQS microservice architecture with auto-scaling policies to handle unlimited throughput",
      "Built a dedicated USPS validation service with fallback retry and correction logic to ensure maximum address accuracy",
      "Developed a variable-data personalization engine and a modular CRM connector layer for seamless HubSpot & Salesforce integration",
      "Implemented WebSockets + DynamoDB streams for a real-time analytics dashboard, providing campaign tracking and engagement insights"
    ],
    "results": [
      "Enabled unlimited direct-mail campaign throughput with zero per-campaign caps",
      "Maintained a 99.8% delivery success rate through USPS-verified addresses and automated retries",
      "Reduced campaign setup and launch time by over 50% with the React drag-and-drop builder",
      "Streamlined contact management by automating sync with HubSpot & Salesforce, eliminating manual imports",
      "Delivered sub-second analytics and reporting, empowering data-driven campaign optimizations"
    ]
  },{
    "id": "vendorful",
    "title": "Vendorful",
    "description": "LiveView sourcing suite delivering sub-second RFP workflows and US$ 4.7 M in funding.",
    "kpi": [
      "Sub-second Latency",
      "$2.25 M Seed",
      "$2.47 M Convertible Note",
      "$1.5 M ARR"
    ],
    "featuredKpi": "$1.5 M ARR",
    "vertical": "Enterprise Procurement",
    "stack": [
      "Elixir",
      "Phoenix LiveView",
      "Oban",
      "PostgreSQL",
      "AWS",
      "GitHub Actions"
    ],
    "link": "https://vendorful.com",
    "image": "/projectScreens/vendorful.png",
    "year": 2019,
    "story": "As Lead Software Engineer, I spearheaded Vendorful's migration from a client-side React UI to server-rendered Phoenix LiveView, architecting real-time channels for live RFP/RFQ events, integrating Oban for background job processing, and establishing GitHub Actions CI/CD with AWS auto-scaling—delivering an enterprise-grade sourcing platform with sub-second response times and 99.9% uptime.",
    "challenges": [
      "Migrating a rich, client-side React application to server-rendered LiveView without UX regressions",
      "Achieving sub-second latency under high concurrency for complex sourcing workflows",
      "Orchestrating real-time reverse auctions and supplier interactions via LiveView channels",
      "Ensuring reliable background processing and observability at enterprise scale"
    ],
    "solutions": [
      "Rewritten core UI in Phoenix LiveView, leveraging its diff-driven DOM updates for instant interactions",
      "Built optimized LiveView components and caching layers to sustain sub-second RFP workflows",
      "Integrated Oban for robust async tasks (report generation, notifications) with PostgreSQL",
      "Implemented blue-green deployments via GitHub Actions and tuned AWS auto-scaling for 99.9% uptime"
    ],
    "results": [
      "Delivered sub-second latency for all core sourcing workflows, reducing cycle times by 90%",
      "Secured $2.25 M seed in Dec 2019 and $2.47 M convertible note in Nov 2021—$4.72 M total funding",
      "Surpassed $1.5 M ARR by 2024, reflecting strong product-market fit and customer retention",
      "Onboarded 10,000+ suppliers and centralized vendor data to drive 18% average savings per event (industry benchmark)",
      "Maintained 99.9% uptime under peak loads with zero critical incidents"
    ]
  },{
    "id": "movebooth",
    "title": "Movebooth",
    "description": "Real-time media booth app tripling throughput and boosting guest engagement 45 %.",
    "kpi": [
      "Guest Engagement +45%",
      "Processing Time –60%",
      "Throughput ×3",
      "Data-Entry Effort –75%",
      "Customization Usage +40%",
      "Delivery Reach 95%",
      "Latency –80%",
      "NPS 97%"
    ],
    "featuredKpi": "NPS 97%",
    "vertical": "Event Marketing",
    "stack": [
      "React Native",
      "React",
      "Ruby on Rails",
      "AWS Lambda",
      "FFmpeg",
      "Twilio API",
      "WhatsApp API"
    ],
    "link": "https://movebooth.co",
    "image": "/projectScreens/movebooth.png",
    "year": 2019,
    "story": "From 2017–2019, I led development of Movebooth's cross-platform React Native app and backend infrastructure, delivering a turnkey media booth solution that empowered brands to instantly generate and share animated GIFs, story-style videos, and high-resolution stills. I migrated image-processing workflows to AWS Lambda with FFmpeg pipelines—tripling throughput and cutting processing time by 60%—built a Ruby on Rails backend for CRM data capture and CSV exports, and developed a React dashboard for remote branding controls and embedded marketing widgets. Finally, I integrated Twilio and WhatsApp APIs for reliable multi-channel distribution, extending delivery reach to 95%, slashing latency by 80%, and achieving a 97% NPS score across major deployments.",
    "challenges": [
      "Delivering reliable, real-time capture and instant sharing of diverse media formats at high-volume events",
      "Migrating synchronous media processing to a scalable, serverless architecture without downtime",
      "Designing a flexible remote branding and marketing widget system for on-the-fly event customization",
      "Ensuring seamless CRM data capture and multi-channel distribution with sub-second delivery"
    ],
    "solutions": [
      "Developed a React Native app paired with on-device FFmpeg processing for efficient media capture",
      "Migrated media pipelines to AWS Lambda functions triggered by S3 events, tripling throughput and reducing processing time by 60%",
      "Built a Ruby on Rails backend with CSV export capabilities for CRM integration and a React dashboard for real-time branding updates",
      "Integrated Twilio and WhatsApp APIs to automate SMS and chat delivery, slashing latency by 80% and extending reach to 95% of guests"
    ],
    "results": [
      "Boosted guest engagement by 45% and doubled real-time share rates",
      "Achieved a 3× increase in processing throughput and 60% reduction in average processing time",
      "Cut data-entry effort by 75% and accelerated campaign launches by 2 days",
      "Increased customization usage by 40% via remote branding controls",
      "Maintained 95% delivery reach with multi-channel distribution and 80% lower latency",
      "Recorded a 97% NPS customer satisfaction score across deployments"
    ]
  },{
    "id": "joineight",
    "title": "JoinEight",
    "description": "AI recruiting extension cutting manual sourcing 30 % and doubling pipeline velocity.",
    "kpi": [
      "Sourcing –30%",
      "Pipeline +50%",
      "Time & Cost-per-Hire –85%",
      "US$ 5 M Seed"
    ],
    "featuredKpi": "Time & Cost-per-Hire –85%",
    "vertical": "HR Technology",
    "stack": [
      "Ruby on Rails",
      "PostgreSQL",
      "JavaScript",
      "Chrome Extension",
      "Python",
      "TensorFlow",
      "AWS",
      "Azure"
    ],
    "link": "https://joineight.ai",
    "image": "/projectScreens/joineight.png",
    "year": 2018,
    "story": "Founded in July 2018, JoinEight launched its AI-powered sourcing platform to automate candidate pipeline building and diversity analytics for enterprise hiring teams. I architected the backend services in Ruby on Rails with PostgreSQL and led development of a Chromium-based extension for real-time LinkedIn profile scraping. Over the following years, I integrated predictive machine learning models and D&I dashboards—driving adoption by top recruitment teams and establishing JoinEight as a leader in AI-driven hiring intelligence.",
    "challenges": [
      "Automating manual, time-consuming candidate sourcing workflows at scale",
      "Ensuring high accuracy in candidate matching and diversity analytics",
      "Building a robust Chrome extension compatible with continuous LinkedIn UI updates",
      "Maintaining real-time data synchronization across distributed services and analytics pipelines"
    ],
    "solutions": [
      "Developed a Chrome extension with resilient DOM-scraping routines and a middleware API in Ruby on Rails to pipeline profile data",
      "Implemented AI-driven candidate scoring models using Python and TensorFlow for predictive fit and diversity metrics",
      "Orchestrated real-time updates with WebSockets and message queues to keep dashboards in sync",
      "Leveraged AWS and Azure for scalable infrastructure and integrated Marketo and Confluence for seamless workflow management"
    ],
    "results": [
      "Reduced manual sourcing effort by 30% and accelerated pipeline building by 50%",
      "Cut overall time-to-hire and cost-per-hire by 85% through AI-optimized workflows",
      "Secured US$ 5 M in seed funding based on early traction and platform roadmap",
      "Established JoinEight as the first AI platform for automated funnel building, driving enterprise adoption from 2018 through 2024"
    ]
  },
  {
    "id": "pertemps",
    "title": "Pertemps",
    "description": "Digital staffing SaaS lifting placement rates 35 % via automated matching and scheduling.",
    "kpi": [
      "Placement Rates +35%",
      "30 000+ Weekly Temp Placements",
      "12 000 Annual Permanent Hires"
    ],
    "featuredKpi": "30 000+ Weekly Temp Placements",
    "vertical": "Recruitment Services",
    "stack": [
      "Laravel",
      "Vue.js",
      "MySQL",
      "AWS (EC2, S3)",
      "GitHub Actions"
    ],
    "link": "https://pertemps.co.uk",
    "image": "/projectScreens/pertemps.png",
    "year": 2018,
    "story": "Pertemps is a unified recruitment platform that streamlines every step of hiring—from data management and job discovery to compensation insights and branch support. I led development of a private candidate repository with advanced CRUD and search, built a responsive self-service portal for CV uploads and real-time alerts, and integrated interactive salary benchmarking and specialist brand routing to boost candidate engagement and recruiter efficiency :contentReference[oaicite:0]{index=0}.",
    "challenges": [
      "Fragmented candidate data across multiple systems, slowing search and matching workflows",
      "Manual application and interview scheduling processes creating administrative bottlenecks",
      "Lack of visibility into placement performance undermining stakeholder confidence",
      "Geographic complexity in branch discovery and in-person support"
    ],
    "solutions": [
      "Engineered a centralized candidate repository with full-text search and filters to unify data management :contentReference[oaicite:1]{index=1}",
      "Developed a unified job portal with shortlist functionality and real-time alerts to automate matching workflows :contentReference[oaicite:2]{index=2}",
      "Implemented an interactive salary health-check tool benchmarking pay against industry standards for candidate empowerment :contentReference[oaicite:3]{index=3}",
      "Built a responsive self-service portal for CV uploads, job alerts, and application tracking to increase mobile engagement :contentReference[oaicite:4]{index=4}",
      "Integrated a specialist brand routing engine covering 20+ verticals to improve match relevance :contentReference[oaicite:5]{index=5}",
      "Launched a geolocation-powered branch locator for seamless in-person support",
      "Deployed a searchable News & Resource Centre to engage candidates and clients",
      "Set up a live placement metrics dashboard displaying 30 000+ weekly temp placements and 12 000 annual permanent hires :contentReference[oaicite:6]{index=6}"
    ],
    "results": [
      "Achieved a 35% increase in overall placement rates",
      "Powered 30 000+ weekly temporary placements and 12 000 annual permanent hires with real-time transparency :contentReference[oaicite:7]{index=7}",
      "Reduced recruiter pipeline management time through automation and self-service tools",
      "Strengthened market leadership with live credibility metrics on key site pages"
    ]
  },
  {
    "id": "myintellirent",
    "title": "MyIntelliRent",
    "description": "Rental-screening SaaS cutting approval time 40 % and filling vacancies within 24 h.",
    "kpi": [
      "Screening Time –40%",
      "Marketing Time –50%",
      "Vacancy Time ≤24 h",
      "99.9% Uptime"
    ],
    "featuredKpi": "Vacancy Time ≤24 h",
    "vertical": "Property Management",
    "stack": [
      "Ruby on Rails",
      "PostgreSQL",
      "Heroku",
      "AWS",
      "Google Maps API",
      "Sidekiq",
      "Haml",
      "Sass",
      "Trello"
    ],
    "link": "https://myintellirent.com",
    "image": "/projectScreens/myintellirent.png",
    "year": 2017,
    "story": "MyIntelliRent unifies marketing, screening, and team collaboration into one SaaS hub for property managers. By automating listing syndication, tenant verifications, and communication workflows, the platform halved vacancy times and enabled units to fill within 24 hours while supporting thousands of simultaneous users without downtime.",
    "challenges": [
      "Fragmented marketing workflows requiring manual syndication to dozens of listing sites",
      "Labor-intensive tenant screening via phone and fax slowing lease approvals",
      "Maintaining platform reliability and performance under rapid user growth"
    ],
    "solutions": [
      "Built a one-click syndication engine to publish listings to 80+ sites, slashing marketing effort by 50%",
      "Designed automated credit, background, and income-verification pipelines to eliminate manual steps",
      "Refactored legacy code and resolved 40+ critical bugs to uphold 99.9% uptime and streamline feature delivery"
    ],
    "results": [
      "Reduced average screening time by 40% and marketing workflow time by 50%",
      "Halved vacancy durations, filling units within 24 hours",
      "Sustained 99.9% platform uptime through comprehensive testing and proactive bug resolution",
      "Platform acquired by Electro Rent in August 2018; later sold to Sandbrook Capital in February 2025"
    ]
  }    
];

// Technology badge colors
export const techColors: Record<string, string> = {
  React: "bg-blue-100 text-blue-800 border-blue-200",
  "React Native": "bg-blue-100 text-blue-800 border-blue-200",
  Node: "bg-green-100 text-green-800 border-green-200",
  "Node.js": "bg-green-100 text-green-800 border-green-200",
  Elixir: "bg-purple-100 text-purple-800 border-purple-200",
  Phoenix: "bg-purple-100 text-purple-800 border-purple-200",
  Solidity: "bg-slate-100 text-slate-800 border-slate-200",
  Ethereum: "bg-slate-100 text-slate-800 border-slate-200",
  Rails: "bg-red-100 text-red-800 border-red-200",
  Laravel: "bg-red-100 text-red-800 border-red-200",
  AWS: "bg-orange-100 text-orange-800 border-orange-200",
  PostgreSQL: "bg-blue-100 text-blue-800 border-blue-200",
  MongoDB: "bg-green-100 text-green-800 border-green-200",
  Firebase: "bg-yellow-100 text-yellow-800 border-yellow-200",
  GraphQL: "bg-pink-100 text-pink-800 border-pink-200",
  "Next.js": "bg-black text-white border-gray-800",
  Vue: "bg-emerald-100 text-emerald-800 border-emerald-200",
  "Vue.js": "bg-emerald-100 text-emerald-800 border-emerald-200",
  FFmpeg: "bg-gray-100 text-gray-800 border-gray-200",
  "Web3.js": "bg-indigo-100 text-indigo-800 border-indigo-200",
  SQS: "bg-orange-100 text-orange-800 border-orange-200"
};

// Helper function to group projects by technology
export const groupProjectsByTechnology = (projects: Project[]): Record<string, Project[]> => {
  const groups: Record<string, Project[]> = {
    all: [...projects],
  };
  
  // Find all unique technologies
  const allTechnologies = new Set<string>();
  projects.forEach(project => {
    project.stack.forEach(tech => {
      allTechnologies.add(tech);
    });
  });
  
  // Group projects by primary technologies
  const primaryTechs = ["React", "Elixir", "Solidity", "Node.js", "Rails", "Laravel"];
  primaryTechs.forEach(tech => {
    if (allTechnologies.has(tech)) {
      groups[tech] = projects.filter(project => 
        project.stack.includes(tech)
      );
    }
  });
  
  return groups;
}; 